// -----------------------------------------------------------
// FloraIQ - Operational Flow Stepper (Aba 3: Fluxo Operacional)
// -----------------------------------------------------------

import { type FC, useState } from "react";
import { ladmOperationalFlow } from "@/mocks/ladm";
import LadmStatusBadge from "./LadmStatusBadge";
import type { LadmBadgeVariant } from "./LadmStatusBadge";

const statusIcon: Record<string, string> = {
  concluido: "ri-check-line",
  em_andamento: "ri-loader-4-line",
  pendente: "ri-time-line",
};

const statusColor: Record<string, string> = {
  concluido: "bg-green-500 text-white",
  em_andamento: "bg-blue-500 text-white",
  pendente: "bg-background-200 text-foreground-400",
};

const statusBadgeMap: Record<string, LadmBadgeVariant> = {
  concluido: "concluido",
  em_andamento: "em_andamento",
  pendente: "pendente",
};

const OperationalFlowStepper: FC = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const lastConcludedIndex = ladmOperationalFlow.reduce((lastIdx, step, i) => {
    if (step.status === "concluido") return i;
    return lastIdx;
  }, -1);

  const activeIndex = ladmOperationalFlow.findIndex(s => s.status === "em_andamento");

  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground-600">
        O fluxo operacional da metodologia FloraIQ-LADM organiza o cadastramento arbóreo em 7 etapas, 
        desde o planejamento até a publicação e gestão dos dados.
      </p>

      <div className="bg-white rounded-card border border-background-200/70 p-6">
        {/* Stepper horizontal — desktop */}
        <div className="hidden lg:flex items-start justify-between mb-8 relative">
          {/* Connector line */}
          <div className="absolute top-5 left-[5%] right-[5%] h-0.5 bg-background-200 z-0"></div>
          {ladmOperationalFlow.map((step, i) => {
            let connectorFill = "bg-transparent";
            if (i <= lastConcludedIndex) connectorFill = "bg-green-500";
            else if (i === activeIndex) connectorFill = "bg-blue-500";
            return (
              <div key={step.step} className="flex flex-col items-center relative flex-1 z-10">
                {i > 0 && (
                  <div className="absolute top-5 right-1/2 w-full h-0.5 -z-0" style={{ background: "transparent" }}></div>
                )}
                <div className="relative mb-2">
                  {i <= lastConcludedIndex && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-[8px]"></i>
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${statusColor[step.status]} transition-all`}>
                    {step.status === "em_andamento" ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className={statusIcon[step.status]}></i>
                    )}
                  </div>
                </div>
                <span className="text-xs font-medium text-foreground-700 mt-1 text-center leading-tight max-w-[100px]">{step.title}</span>
                <span className="text-xs text-foreground-400 mt-0.5">{step.progress}%</span>
              </div>
            );
          })}
        </div>

        {/* Connector progress overlay for desktop */}
        <div className="hidden lg:block absolute top-5 left-[5%] h-0.5 bg-green-500 z-[5]" style={{ 
          width: lastConcludedIndex >= 0 ? `${(lastConcludedIndex + 1) / ladmOperationalFlow.length * 90}%` : "0%",
          transition: "width 1s ease-in-out" 
        }}></div>

        {/* Lista de etapas — mobile + desktop accordion */}
        <div className="space-y-3">
          {ladmOperationalFlow.map((step) => {
            const isExpanded = expandedStep === step.step;
            return (
              <div key={step.step} className="border border-background-200/70 rounded-card overflow-hidden transition-all duration-200">
                <button
                  onClick={() => setExpandedStep(isExpanded ? null : step.step)}
                  className="w-full flex items-center gap-4 p-4 cursor-pointer hover:bg-background-50/60 transition-colors text-left"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${statusColor[step.status]} transition-all`}>
                    {step.status === "em_andamento" ? (
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                    ) : (
                      <i className={`${step.icon} text-sm`}></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground-900 font-heading">Etapa {step.step}: {step.title}</span>
                      <LadmStatusBadge variant={statusBadgeMap[step.status]} />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-foreground-500 mt-1 flex-wrap">
                      <span><i className="ri-user-3-line mr-1 text-foreground-400"></i>{step.responsavel}</span>
                      {step.pendencias > 0 && <span className="text-orange-600"><i className="ri-alert-line mr-1"></i>{step.pendencias} pendências</span>}
                      {step.documentosVinculados > 0 && <span><i className="ri-file-text-line mr-1 text-foreground-400"></i>{step.documentosVinculados} documentos</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-20 h-1.5 rounded-full bg-background-200 hidden sm:block">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-700 ${
                          step.status === "concluido" ? "bg-green-500" : step.status === "em_andamento" ? "bg-blue-500" : "bg-background-300"
                        }`}
                        style={{ width: `${step.progress}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 w-8 text-right">{step.progress}%</span>
                    <i className={isExpanded ? "ri-arrow-up-s-line text-foreground-400" : "ri-arrow-down-s-line text-foreground-400"}></i>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-background-200/50 px-4 py-3 bg-background-50/50 slide-up">
                    <p className="text-xs font-medium text-foreground-700 mb-2">Atividades:</p>
                    <ul className="space-y-1.5">
                      {step.activities.map((act, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-foreground-600">
                          <i className="ri-checkbox-blank-circle-fill text-[6px] text-foreground-400 mt-1.5 flex-shrink-0"></i>
                          {act}
                        </li>
                      ))}
                    </ul>
                    <button className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap">
                      Abrir etapa completa <i className="ri-external-link-line ml-1"></i>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OperationalFlowStepper;